import * as React from "react";
import { ModalConfirmation } from "@adsk/alloy-react-modal";
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from "@adsk/alloy-react-notification";
import { useCurrentProjectId } from "@adsk/alloy-provider";
import { Issue } from "@adsk/acc-issues-types";
import { Template } from "@adsk/acc-issues-common/components/form/IssueExportPanelBody/constants";
import { createAndPollIssueReport } from "../../report-service/createAndPollIssueReport";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useUser } from "@adsk/acc-issues-core/utils/hooks/alloyHooks";
import { useContext } from "react";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";
import { trackQRPReportConfirm } from "../analytics/issueActionsTracking";
import { FileFormat } from "@adsk/acs-reports-sdk";

export const ReportConfirmationModal = ({
  isOpen = false,
  issue,
  closeReportModal,
}: {
  isOpen: boolean;
  issue?: Issue;
  closeReportModal: () => void;
}) => {
  const { T } = useTranslations();
  const projectId = useCurrentProjectId()!;
  const { showNotification } = useNotifications();
  const { user } = useUser();
  const email =
    (user && user.email) || (T.translate("reports.yourEmail") as string);
  const isDeletedSpace = useContext(DeletedIssuesSpaceContext);

  if (!issue) {
    // This shouldn't happen, but in case it does we want to show an error notification
    if (isOpen) {
      showNotification({
        status: NOTIFICATION_STATUSES.ERROR,
        message: T.translate("reports.reportForNoIssueErrorMessage"),
      });
    }
    return null;
  }

  return (
    <ModalConfirmation
      style={{}}
      show={isOpen}
      title={T.translate("reports.modalTitle") as string}
      message={T.translate("reports.modalMessage", {
        email,
        displayId: issue.displayId,
        title: issue.title,
      })}
      onConfirm={async () => {
        trackQRPReportConfirm({ isIssueDeleted: isDeletedSpace });
        const reportFilter: { field: string; value: any }[] = [
          { field: "id", value: issue.id },
        ];
        if (issue.deleted) {
          reportFilter.push({ field: "deleted", value: "true" });
        }
        const attributes = {
          title: T.translate("reports.templates.detail.reportTitle", {
            title: issue.title,
          }) as string,
          format: FileFormat.PDF,
          template: Template.DETAIL,
          options: {
            filter: reportFilter,
          },
        };
        closeReportModal();
        createAndPollIssueReport({
          attributes,
          projectId,
          email,
          isDeletedSpace,
          handleNotification: showNotification,
          showInfoNotification: true,
        });
      }}
      onClose={closeReportModal}
      confirmationButtonLabel={T.translate("actions.export") as string}
      isConfirmationButtonAlert={false}
      data-dd-action-name="<MASKED-DATA>"
    />
  );
};
